import { createTranslator, type Locale } from "./i18n";
import { researchT, type ResearchTranslationKey } from "./research-i18n";
import { productFocusT } from "./product-focus";
import { researchRunViewModels, buildResearchComparison } from "./research-ui-model";
import { formatLocalizedCurrency, formatLocalizedNumber, formatLocalizedPercent } from "./locale-format";
import type { HistoryItem } from "./types";

/** Uses the same provenance checks as Research; missing data never implies a controlled experiment. */
export function SavedRunComparison({ baseline, candidate, locale }: { baseline: HistoryItem; candidate: HistoryItem; locale: Locale }) {
  const t = createTranslator(locale);
  const items = [baseline, candidate];
  const runs = researchRunViewModels(items.map((item) => ({
    id: item.id, historyItemID: item.id, createdAt: item.createdAt, evidenceState: "not_evaluated" as const,
  })), items);
  const comparison = buildResearchComparison(runs, items.map((item) => item.id), baseline.id);
  if (!comparison) return null;
  const levelKeys: Record<string, ResearchTranslationKey> = { exact: "exact", controlled: "controlled", exploratory: "exploratory", limited: "limited", not_comparable: "notComparable" };
  const helpKeys: Record<string, ResearchTranslationKey> = { exact: "exactHelp", controlled: "controlledHelp", exploratory: "exploratoryHelp", limited: "limitedHelp", not_comparable: "notComparableHelp" };
  const display = (value: number | undefined, format: (value: number) => string) =>
    typeof value === "number" && Number.isFinite(value) ? format(value) : "—";
  return <section className="saved-run-comparison" aria-label={productFocusT(locale, "compare")}>
    <h3>{productFocusT(locale, "compare")}</h3>
    <strong>{researchT(locale, levelKeys[comparison.comparison.level])}</strong>
    <p>{researchT(locale, helpKeys[comparison.comparison.level])}</p>
    {comparison.comparison.level === "not_comparable" ? <p>{researchT(locale, "metricsHidden")}</p> :
      <div className="comparison-scroll"><table><thead><tr><th>{t("strategy")}</th><th>{researchT(locale, "baseline")}</th><th>{t("results")}</th></tr></thead>
        <tbody>
          <tr><th>{t("symbol")}</th>{runs.map((run) => <td key={run.link.id}>{run.comparable.symbol || "—"} · {run.comparable.timeframe || "—"}</td>)}</tr>
          <tr><th>{t("netProfit")}</th>{runs.map((run) => <td key={run.link.id}>{display(run.comparable.metrics.netProfitAmount, (value) => formatLocalizedCurrency(value, locale))}</td>)}</tr>
          <tr><th>{t("drawdown")}</th>{runs.map((run) => <td key={run.link.id}>{display(run.comparable.metrics.maxDrawdownPct, (value) => formatLocalizedPercent(value, locale, 1))}</td>)}</tr>
          <tr><th>{t("trades")}</th>{runs.map((run) => <td key={run.link.id}>{display(run.comparable.metrics.tradeCount, (value) => formatLocalizedNumber(value, locale))}</td>)}</tr>
        </tbody>
      </table></div>}
  </section>;
}
